import PropTypes from 'prop-types';
import React from 'react';
import { compact } from 'lodash';
import {
  HashUtil,
  APIRequest,
  CategoryTree,
} from '@reverbdotcom/discovery-ui';

import InferCategoryQuery from './category_select/infer_category_query';
import Row from './category_select/row';
import TraitSelect from './listing_form/trait_select';

const MAX_SELECTED_CATEGORIES = 2;
const LISTING_DATA_ATTRIBUTE = 'data-listing-form';
const MAKE_SELECTOR = `[${LISTING_DATA_ATTRIBUTE}="make"]`;
const MODEL_SELECTOR = `[${LISTING_DATA_ATTRIBUTE}="model"]`;
const SHIPPING_PROFILE_SELECTOR = '[data-shipping-profile-select]';

const noCategoriesSelected = [''];

const setShippingProfileSelected = value =>
  $(SHIPPING_PROFILE_SELECTOR).val(`${value}`)
    .trigger('liszt:updated')
    .trigger('change');

class CategorySelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedCategoryIds: this.selectedCategoryIdsInOrder(),
      selectedCategoryUuids: this.props.selectedCategoryUuids,
      loaded: true,
      userHasSelectedCategory: props.selectedCategoryIds.length !== 0,
      previousCategoryQuery: new InferCategoryQuery('', ''),
    };

    this.loadAnotherRow = this.loadAnotherRow.bind(this);
  }

  componentDidMount() {
    $(document).on('blur', [MAKE_SELECTOR, MODEL_SELECTOR].join(', '), () => this.inferCategory());
  }

  getNewSelectedIds(categoryId, index) {
    if (categoryId.trim() !== '') {
      return this.newSelectedIdsFromSelected(categoryId, index);
    }
    if (index > 0) {
      return this.removeSubcategory(index);
    }
    return noCategoriesSelected;
  }

  updateShippingProfile(selectedIds) {
    if (this.props.persisted) { return false; }

    let ids = compact(selectedIds);
    if (ids.length > 0) {
      ids = this.addRootCategoryId(ids);
      return this.fetchAndSetShippingProfile(ids);
    }
    return setShippingProfileSelected('');
  }

  addRootCategoryId(ids) {
    const categories = this.categoryTree().findByIds(ids);
    // if the root isn't selected, add it to the list of ids to send
    if (categories.every(cat => !cat.isRoot)) {
      ids.push(categories[0].root.id);
    }
    return ids;
  }

  fetchAndSetShippingProfile(ids) {
    if ($(SHIPPING_PROFILE_SELECTOR).length > 0) {
      const req = APIRequest.get(this.props.shippingProfileUrl, { category_ids: ids });

      req.then((data) => {
        setShippingProfileSelected(data.category_id);
      });
    }
  }

  shouldInferCategory(query) {
    return !this.state.userHasSelectedCategory &&
      query.hasAllParts &&
      !this.state.previousCategoryQuery.sameAs(query);
  }

  inferCategory() {
    const query = new InferCategoryQuery($(MAKE_SELECTOR).val(), $(MODEL_SELECTOR).val());

    if (this.shouldInferCategory(query)) {
      this.setState({ loaded: false, previousCategoryQuery: query });
      const req = query.fetch();
      req.then((data) => {
        const category = this.categoryTree().findById(data.category_id);
        this.setState({
          loaded: true,
          selectedCategoryIds: [data.category_id || ''],
          selectedCategoryUuids: [category && category.attributes.uuid]
        });
      });
      req.always(() => {
        this.setState({ loaded: true });
      });
    }
  }

  initialCategory() {
    if (this.state.selectedCategoryIds[0]) {
      return this.categoryTree().findById(this.state.selectedCategoryIds[0]);
    }
    return null;
  }

  // start with the least specific first
  selectedCategoryIdsInOrder() {
    const ids = this.props.selectedCategoryIds;
    if (!ids.length) {
      return noCategoriesSelected;
    }

    if (ids.length === 1) {
      return ids;
    }

    return this.props.selectedCategoryIds.sort((a, b) => {
      const catA = this.categoryTree().findById(a);
      const catB = this.categoryTree().findById(b);

      // safely handle an edge case where one of the category ids
      // is a middle category which is not part of the JS category tree
      const catADepth = HashUtil.getSafely(catA, 'ancestors.length', Number.MAX_VALUE);
      const catBDepth = HashUtil.getSafely(catB, 'ancestors.length', Number.MAX_VALUE);

      return catADepth - catBDepth;
    });
  }

  loadAnotherRow(e) {
    e.preventDefault();
    this.setState({ selectedCategoryIds: [...this.state.selectedCategoryIds, ''] });
  }

  categorySelected(categoryId, index) {
    const newSelectedIds = this.getNewSelectedIds(categoryId, index);

    this.updateShippingProfile(newSelectedIds);
    this.setState({
      selectedCategoryIds: newSelectedIds,
      selectedCategoryUuids: this.categoryTree().findByIds(newSelectedIds).map(cat => cat.attributes.uuid),
      userHasSelectedCategory: newSelectedIds.length > 0,
    });
  }

  removeSubcategory(index) {
    const newSelectedIds = this.state.selectedCategoryIds.slice(0);
    newSelectedIds.splice(index, 1);
    return newSelectedIds;
  }

  newSelectedIdsFromSelected(categoryId, index) {
    const currentSelectedCategory = this.categoryTree()
      .findById(this.state.selectedCategoryIds[index]);
    const newSelectedCategory = this.categoryTree().findById(categoryId);
    // clone the array
    let newSelectedIds = this.state.selectedCategoryIds.slice(0);

    newSelectedIds[index] = categoryId;

    // if we've selected something from a different branch, remove the selects below it
    if (!newSelectedCategory ||
        (currentSelectedCategory && !currentSelectedCategory.isInSameBranch(newSelectedCategory))) {
      newSelectedIds = newSelectedIds.slice(0, index + 1);
    }
    return newSelectedIds;
  }

  categoryTree() {
    return new CategoryTree(this.props.categories);
  }

  categories() {
    return this.categoryTree().flattened();
  }

  categoriesToRender(index) {
    let cats;
    if (index === 0 || !this.initialCategory()) {
      cats = this.categories();
    } else {
      cats = this.initialCategory().inSameBranch
        .filter(cat => cat.id !== this.initialCategory().id);
    }

    const allowedCategories = cats.filter(cat => cat.isSelectableCategory);

    return allowedCategories;
  }

  initialCategoryIsNotRoot() {
    return this.initialCategory() && !this.initialCategory().isRoot;
  }

  canHaveMoreRows() {
    return this.state.selectedCategoryIds.length !== MAX_SELECTED_CATEGORIES;
  }

  canShowAddButton() {
    if (this.props.hidden) { return false; }

    return this.initialCategoryIsNotRoot() && this.canHaveMoreRows();
  }

  selectedCategoryRows() {
    return this.state.selectedCategoryIds.map((categoryId, index) =>
      (<Row
        disabled={!this.state.loaded}
        categoryIdSelected={categoryId}
        inputName={this.props.inputName}
        categorySelected={(id, ix) => this.categorySelected(id, ix)}
        key={`row-${categoryId}`}
        categoryIndex={index}
        categories={this.categoriesToRender(index)}
      />));
  }

  renderHiddenFields() {
    return this.state.selectedCategoryIds.map(categoryId =>
      (<input
        type="hidden"
        value={categoryId}
        key={categoryId}
        ref="categorySelect"
        name={this.props.inputName}
        data-pg-match="required"
      />));
  }

  renderAddCategoryButton() {
    if (!this.canShowAddButton()) { return null; }

    return (
      <a href="#" onClick={this.loadAnotherRow}>
        <span className="fa fa-plus ml-half mr-half" />
        <b>
          {I18n.t('categorySelect.addCategory')}
        </b>
      </a>
    );
  }

  render() {
    if (this.props.hidden) {
      return (
        <div>
          {this.renderHiddenFields()}
        </div>
      );
    }

    return (
      <div className="form-group">
        <div className="form-group__header">
          <label htmlFor={this.props.inputName}>{I18n.t('categorySelect.categoryLabel')}</label>
        </div>
        <div className="form-group__fields">
          {this.selectedCategoryRows()}
          {this.renderAddCategoryButton()}
          <TraitSelect
            categoryUuids={this.state.selectedCategoryUuids}
            traits={this.props.traits}
            blankForm={this.props.traits && this.props.traits.length === 0}
            persistedForm={this.props.persisted}
            hasCanonicalProduct={this.props.hasCanonicalProduct}
          />
        </div>
      </div>
    );
  }
}

CategorySelect.propTypes = {
  selectedCategoryIds: PropTypes.arrayOf(PropTypes.number),
  shippingProfileUrl: PropTypes.string.isRequired,
  persisted: PropTypes.bool,
  inputName: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  hidden: PropTypes.bool,
  traits: PropTypes.arrayOf(PropTypes.object),
  selectedCategoryUuids: PropTypes.arrayOf(PropTypes.string),
  hasCanonicalProduct: PropTypes.bool,
};

CategorySelect.defaultProps = {
  selectedCategoryIds: noCategoriesSelected,
  persisted: false,
  hidden: false,
  selectedCategoryUuids: [],
  hasCanonicalProduct: false,
};

export default CategorySelect;
